import * as _ from 'underscore';

import { IConnectedViewInfo, IMetricInfo } from '../../metrics/models/IMetricInfo';
import { IUserInfo } from '../models/IUserInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { IConnectedView, IMetric, IMetricDetail } from '../ServerApi/types/IMetric';
import { IMetricUsageInfo } from '../ServerApi/types/IUsageInfo';
import { MapperHelper } from './MapperHelper';
import { ProjectMapper } from './ProjectMapper';
import { UserMapper } from './UserMapper';
import { ContentMapper } from './ContentMapper';
import { FeatureFlags } from '../FeatureFlags';
import { ILocationInfo } from '../models/ILocationInfo';

export module MetricMapper {
  const M = MapperHelper;

  export function map(
    serverApiMetrics: IMetric[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    locations: ILocationInfo[] = [],
    connectedViews: _.Dictionary<IConnectedViewInfo>,
    usageInfoMaxValues?: IMetricUsageInfo,
    favoriteMetrics: _.Dictionary<any> = {}
  ): IMetricInfo[] {
    const metricsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.metric, projects);
    const metricsToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.metric, locations);
    return serverApiMetrics.map((m: IMetric) => {
      return mapOne(m, users[m.ownerId],
        (FeatureFlags.refactorProjectEnabled) ? metricsToProject[m.id] : projects[m.projectId],
        connectedViews[m.connectedViewPath], usageInfoMaxValues, !!favoriteMetrics[m.id],
        (FeatureFlags.personalSpaceEnabled ? metricsToLocation[m.id] : undefined));
    });
  }

  export function mapOne(
    metric: IMetric,
    owner?: IUserInfo,
    project?: INamedItemInfo,
    connectedView?: IConnectedViewInfo,
    usageInfoMaxValues?: IMetricUsageInfo,
    favorite?: boolean,
    location?: ILocationInfo
  ): IMetricInfo {
    return {
      createdAt:                    M.toDate(metric.createdAt),
      updatedAt:                    M.toDate(metric.updatedAt),
      suspended:                    metric.suspended,
      description:                  metric.description,
      id:                           metric.id,
      luid:                         metric.luid,
      name:                         metric.name,
      owner:                        UserMapper.mapOne(owner),
      favorite:                     favorite,
      contentType:                  'metric',
      tags:                         M.sortTags(metric.tags),
      connectedView:                connectedView,
      project:                      project,
      location:                     location,
      usageInfo:                    metric.usageInfo || <IMetricUsageInfo>{},
      usageInfoMaxValues:           usageInfoMaxValues || <IMetricUsageInfo>{},
    };
  }

  export function mapDetail(
    metric: IMetricDetail
  ): IMetricInfo {
    return {
      createdAt:                    M.toDate(metric.createdAt),
      updatedAt:                    M.toDate(metric.updatedAt),
      suspended:                    metric.suspended,
      description:                  metric.description,
      id:                           metric.id,
      luid:                         metric.luid,
      name:                         metric.name,
      owner:                        UserMapper.mapOne(metric.owner),
      favorite:                     metric.favorite,
      contentType:                  'metric',
      tags:                         M.sortTags(metric.tags),
      connectedView:                (metric.connectedViewViewModel && mapOneConnectedView(metric.connectedViewViewModel)),
      project:                      ProjectMapper.mapName(metric.project)
    };
  }

  export function mapConnectedViews(
    serverApiConnectedViews: IConnectedView[]
  ): IConnectedViewInfo[] {
    return serverApiConnectedViews.map((cv: IConnectedView) => mapOneConnectedView(cv));
  }

  export function mapOneConnectedView(
    connectedView: IConnectedView
  ): IConnectedViewInfo {
    return {
      id:                           connectedView.id,
      viewId:                       connectedView.viewId,
      viewLuid:                     connectedView.viewLuid,
      name:                         connectedView.name,
      isViewModified:               connectedView.isViewModified,
      path:                         connectedView.path,
      thumbnailUrl:                 connectedView.thumbnailUrl,
      type:                         connectedView.type
    };
  }

  export function mapFromCRASS(metric: any): IMetricInfo{
    return {
      createdAt:                    M.toDate(metric.createdTime) || M.toDateFromTimeOffsetObject(metric.createdTime),
      updatedAt:                    M.toDate(metric.modifiedTime) || M.toDateFromTimeOffsetObject(metric.modifiedTime),
      suspended:                    metric.suspendState !== 'NotSuspended',
      description:                  metric.description,
      id:                           metric.id + '',
      luid:                         metric.luid,
      name:                         metric.title,
      favorite:                     false,
      contentType:                  'metric',
      tags:                         M.sortTags(metric.tags),
      connectedView:                null,
      project:                      null,
      owner:                        UserMapper.mapOneOwnerFromCRASS(metric),
      usageInfo:                    <IMetricUsageInfo>{},
      usageInfoMaxValues:           <IMetricUsageInfo>{}
    };
  }
}
